import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState, useEffect } from 'react';
import { logRender } from '@one-vision/utils';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Skeleton, } from '@mui/material';
import { csn } from '@one-vision/utils';
import { DzDialog, useApiClient, useGlobalStyles } from '../../shared';
import { withModalManager } from '../../shared/dz-modal-manager';
import { DzChip } from '../../shared/dz-chip';
import { DzCouponInput } from './dz-coupon-input.view';
import { DzReceipt } from './dz-receipt.view';
import { useStyles } from './dz-pre-sold-dialog.styles';
import DzCreditCardForm from '../dz-credit-card-form/dz-credit-card-form.view';
import { CardElement, useElements, useStripe, } from '@stripe/react-stripe-js';
const DEFAULT_MONTHLY_INDEX = 1;
const DEFAULT_YEARLY_INDEX = 2;
const DEFAULT_MEMBERSHIP = 'Priority';
var Interval;
(function (Interval) {
    Interval["month"] = "month";
    Interval["year"] = "year";
})(Interval || (Interval = {}));
const membershipLengthList = [
    { label: '6 month', value: 6, interval: Interval.month },
    { label: '9 month', value: 9, interval: Interval.month },
    { label: '1 year', value: 1, interval: Interval.year },
];
const getOffers = (data) => {
    var _a, _b;
    const filtered = (_a = data.included) === null || _a === void 0 ? void 0 : _a.filter((el) => el.type === 'Product');
    const productNames = (_b = data.included) === null || _b === void 0 ? void 0 : _b.filter((el) => el.type === 'ProductName');
    const offersWithName = filtered.map((el) => {
        const productName = productNames.find((item) => { var _a; return ((_a = el.relationships) === null || _a === void 0 ? void 0 : _a.productName.data[0].id) === item.id; });
        return Object.assign(Object.assign({}, el.attributes), { id: productName === null || productName === void 0 ? void 0 : productName.id, productName: productName === null || productName === void 0 ? void 0 : productName.attributes.productName });
    });
    return offersWithName;
};
export const DzPreSoldDialog = ({ isOpen, close, clear, address, zendesk = false, annual = false, dispatch, actions, }) => {
    const stripe = useStripe();
    const elements = useElements();
    logRender(DzPreSoldDialog);
    const classes = Object.assign(Object.assign({}, useGlobalStyles()), useStyles());
    const [isLoading, setIsLoading] = useState(false);
    const [creditCardError, setCreditCardError] = useState(true);
    const [offers, setOffers] = useState([]);
    const [selectedChip, setSelectedChip] = useState(annual ? membershipLengthList[2] : membershipLengthList[1]);
    const [selectedMembership, setSelectedMembership] = useState();
    const [coupon, setCoupon] = useState('');
    const [primaryEmail, setPrimaryEmail] = useState('');
    const [invoice, setInvoice] = useState(null);
    const api = useApiClient();
    const invoiceGet = (email, interval, productId, pricePointId, couponId) => {
        const params = {
            'address-id': address.ovaid,
            email: decodeURIComponent(email),
            'interval-count': interval,
            'product-id': productId,
            'price-point-id': pricePointId,
            coupon: couponId,
        };
        return api.getUpcomingInvoice(params);
    };
    const invoiceGetRequest = async (interval, productId, pricePointId, couponId) => {
        var _a, _b;
        setIsLoading(true);
        try {
            const { data: invoiceData } = await invoiceGet(primaryEmail, interval, productId, pricePointId, couponId);
            setInvoice(invoiceData.data);
        }
        catch (err) {
            const error = err;
            if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.data.errors[0].title) {
                dispatch(actions.updateSnackbar({
                    type: 'error',
                    text: (_b = error.response) === null || _b === void 0 ? void 0 : _b.data.errors[0].title,
                }));
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleClose = useCallback(() => {
        close();
    }, [close]);
    const getPricePointFieldName = (interval) => interval === Interval.year ? 'annualPricePointId' : 'pricePointId';
    useEffect(() => {
        if (!address) {
            return;
        }
        setIsLoading(true);
        const getInitData = async () => {
            var _a;
            const { data: client } = await api.getClientsWithContacts({
                ovcid: address.primaryClientId,
            });
            const email = (_a = client[0].emails.find(({ clientEmailId }) => clientEmailId === client[0].primaryClientEmailId)) === null || _a === void 0 ? void 0 : _a.email;
            setPrimaryEmail(email);
            const { data } = await api.getAddress({
                filter: { ovaid: address.ovaid },
                include: ['brand.product.productName'],
            });
            const offersWithName = getOffers(data);
            setOffers(offersWithName);
            const defaultOffer = offersWithName.find((el) => el.productName === DEFAULT_MEMBERSHIP);
            setSelectedMembership(defaultOffer);
            const { data: invoiceInit } = await invoiceGet(email, annual
                ? membershipLengthList[DEFAULT_YEARLY_INDEX].value
                : membershipLengthList[DEFAULT_MONTHLY_INDEX].value, defaultOffer.id, defaultOffer[getPricePointFieldName(annual ? Interval.year : Interval.month)], '');
            setInvoice(invoiceInit.data);
            setIsLoading(false);
        };
        getInitData();
    }, [address]);
    const membershipHandler = useCallback((e) => {
        const selectedOffer = offers.find((el) => el.id === e.target.value);
        setSelectedMembership(selectedOffer);
        invoiceGetRequest(selectedChip.value, selectedOffer.id, selectedOffer[getPricePointFieldName(selectedChip.interval)], coupon);
    }, [selectedMembership, selectedChip, offers]);
    const handleChipClick = useCallback((value) => {
        setSelectedChip(value);
    }, [selectedChip]);
    useEffect(() => {
        if (selectedChip && selectedMembership) {
            invoiceGetRequest(selectedChip.value, selectedMembership.id, selectedMembership[getPricePointFieldName(selectedChip.interval)], coupon);
        }
    }, [selectedChip]);
    const loadingHandler = (status) => {
        setIsLoading(status);
    };
    const invoiceHandler = (invoiceData) => {
        setInvoice(invoiceData);
    };
    const handleSubmit = useCallback(async () => {
        setIsLoading(true);
        if (!stripe || !elements) {
            return;
        }
        const card = elements.getElement(CardElement);
        const result = await stripe.createToken(card);
        if (!invoice || !selectedMembership) {
            return;
        }
        const props = {
            addressId: address.ovaid,
            intervalCount: selectedChip.value,
            intervalUnit: selectedChip.interval,
            productId: selectedMembership.id,
            amountDue: invoice.amountDue,
            paymentData: result,
            pricePointId: selectedMembership[getPricePointFieldName(selectedChip.interval)],
        };
        if (coupon) {
            props.coupon = coupon;
        }
        try {
            const { data } = await api.postPreSoldMembership(props);
            close(data.data.id);
        }
        catch (e) {
            console.log(e);
        }
        setIsLoading(false);
    }, [setIsLoading, invoice, selectedMembership, selectedChip, address]);
    const cardElementChangeHandler = useCallback((e) => {
        if (e.complete) {
            setCreditCardError(false);
            return;
        }
        setCreditCardError(true);
    }, [setCreditCardError]);
    const content = useMemo(() => {
        return (_jsxs(Box, Object.assign({ display: "flex", flexDirection: "column", "data-testid": "container" }, { children: [_jsx(Typography, Object.assign({ className: csn(classes.title) }, { children: "Details" })), !offers.length ? (_jsx(Skeleton, { animation: "wave", variant: "rounded", height: 54, width: "100%", className: classes.marginBottom })) : (_jsxs(FormControl, Object.assign({ className: classes.marginBottom }, { children: [_jsx(InputLabel, Object.assign({ id: "membership-level" }, { children: "Membership Level" })), _jsx(Select, Object.assign({ labelId: "membership-level", label: "Membership Level", value: selectedMembership === null || selectedMembership === void 0 ? void 0 : selectedMembership.id, onChange: membershipHandler }, { children: offers.map((el) => (_jsx(MenuItem, Object.assign({ value: el.id }, { children: el.productName }), el.id))) }))] }))), _jsx(Typography, Object.assign({ className: csn(classes.title) }, { children: "Length of Membership" })), _jsx(Box, Object.assign({ className: classes.chipContainer }, { children: membershipLengthList
                        .filter((el) => !annual ? el.interval !== Interval.year : true)
                        .map((el) => (_jsx(DzChip, { className: classes.chip, label: el.label, checked: el.label === selectedChip.label, onClick: () => handleChipClick(el) }, el.label))) })), _jsx(Typography, Object.assign({ className: csn(classes.title, classes.marginTop) }, { children: "Payment Method" })), _jsx(DzCreditCardForm, { onChangeHandler: cardElementChangeHandler }), selectedMembership && (_jsx(DzCouponInput, { loading: isLoading, setIsLoading: loadingHandler, setInvoice: invoiceHandler, coupon: coupon, setCoupon: setCoupon, getRequest: (couponId) => invoiceGet(primaryEmail, selectedChip.value, selectedMembership.id, selectedMembership[getPricePointFieldName(selectedChip.interval)], couponId), dispatch: dispatch, actions: actions })), _jsx(DzReceipt, { address: address, membership: selectedMembership, interval: selectedChip, amountDue: invoice === null || invoice === void 0 ? void 0 : invoice.amountDue, isLoading: isLoading })] })));
    }, [
        selectedChip,
        selectedMembership,
        address,
        invoice,
        coupon,
        annual,
        offers,
        isLoading,
        primaryEmail,
    ]);
    return (_jsx(DzDialog, { isOpen: isOpen, clear: clear, caption: "Add Pre-sold Membership", okButtonText: "Save", okButtonDisabled: isLoading || creditCardError, content: content, onClose: handleClose, onOk: handleSubmit, zendesk: zendesk }));
};
export default withModalManager()(DzPreSoldDialog);
